import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useGetProductionRoutePeriodSummary } from '@/hooks/api/v2/productionRoutes'
import { BreadcrumbsBar, H2, parsePeriodId } from '@/common'
import useI18n from '@/shared/hooks/useI18n'
import { useInstallations } from '@/features/installations/hooks/useInstallations'
import { Container, LoadingScreen } from '@/shared/components'
import { Button } from '@/common/components/Button/Button'
import Plus from '@/common/components/icons/Plus/Plus'
import ProductionRoutePeriodSummary from './componets/ProductionRoutePeriodSummary'
import { Card } from '@/common/components/Card/Card'
import ReportListItem from './componets/ReportListItem'
import { useGetReportsForInstallation } from '@/hooks/api/v2/reports'
import CreateReportDialog from './dialogs/CreateReportDialog'
import { useDialog } from '@/shared/hooks/useDialog'
import CreateFinalizeReportDialog from './dialogs/CreateFinalizeReportDialog'
import { Alert } from '@/common/components/Alert/Alert'
import { useCallback, useState } from 'react'
import DeleteReportDialog from './dialogs/DeleteReportDialog'

export default function ReportsPage() {
  const { t, dateLongFormatterMonthYear } = useI18n()
  const navigate = useNavigate()
  const installationId = useParams<{ installationId: string }>().installationId!
  const productionRouteId = useParams<{ productionRouteId: string }>().productionRouteId!

  const periodId = useParams<{ periodId: string }>().periodId!
  const { startDate, endDate } = parsePeriodId(periodId)

  const reportsQuery = useGetReportsForInstallation(installationId)
  const installationsQuery = useInstallations()
  const installations = installationsQuery.data || []
  const reports = reportsQuery.data || []

  const [reportToDelete, setReportToDelete] = useState<{
    id: string
    formattedReportPeriod: string
  }>({ id: '', formattedReportPeriod: '' })

  const {
    open: isCreateReportDialogOpen,
    openDialog: openCreateReportDialog,
    closeDialog: closeCreateReportDialog
  } = useDialog()

  const {
    open: isFinalizeReportDialogOpen,
    openDialog: openFinalizeReportDialog,
    closeDialog: closeFinalizeReportDialog
  } = useDialog()

  const {
    open: isDeleteReportDialogOpen,
    openDialog: onDeleteReportDialogOpen,
    closeDialog: onDeleteReportDialogClose
  } = useDialog()

  const onDeleteReport = useCallback(
    (reportId: string, formattedReportPeriod: string) => {
      setReportToDelete({ id: reportId, formattedReportPeriod })
      onDeleteReportDialogOpen()
    },
    [onDeleteReportDialogOpen]
  )
  const [hoveredPeriod, setHoveredPeriod] = useState<string | null>(null)

  const reportsForProductionRoutePeriod = reports.filter(report => {
    const reportStartDate = new Date(report.startDate)
    const reportEndDate = new Date(report.endDate)

    const reportStartMonth = reportStartDate.getMonth()
    const reportStartYear = reportStartDate.getFullYear()
    const reportEndMonth = reportEndDate.getMonth()
    const reportEndYear = reportEndDate.getFullYear()

    const periodStartMonth = startDate.getMonth()
    const periodStartYear = startDate.getFullYear()
    const periodEndMonth = endDate.getMonth()
    const periodEndYear = endDate.getFullYear()

    const isWithinPeriod =
      (reportStartYear > periodStartYear ||
        (reportStartYear === periodStartYear && reportStartMonth >= periodStartMonth)) &&
      (reportEndYear < periodEndYear ||
        (reportEndYear === periodEndYear && reportEndMonth <= periodEndMonth))

    return (
      isWithinPeriod && report.productionRouteId === productionRouteId && report.deletedAt === null
    )
  })

  const { data: productionRoutePeriodSummary, isLoading } = useGetProductionRoutePeriodSummary(
    installationId,
    productionRouteId,
    periodId
  )
  const installation = installations?.find(installation => installation.id === installationId)

  if (isLoading || !productionRoutePeriodSummary) {
    return <LoadingScreen />
  }

  if (!installation) {
    return <Navigate to="/dashboard" />
  }

  const reportingPeriodBasis = installation?.basisForReportingPeriod

  const periodLabel =
    reportingPeriodBasis === 'CALENDAR_YEAR'
      ? `${startDate.getFullYear()}`
      : `${dateLongFormatterMonthYear(startDate)} - ${dateLongFormatterMonthYear(endDate)}`

  const methodAggregatedEmissions =
    productionRoutePeriodSummary.methodAggregatedEmissions.length > 0
      ? productionRoutePeriodSummary.methodAggregatedEmissions[0]
      : null

  const coverage = productionRoutePeriodSummary.coverage

  const areAllMonthsReported = coverage.every(month => month.reportSubmitted)

  const navigateToFinalizedReport = (finalizedReportId: string) => {
    navigate(
      `/dashboard/${installationId}/production-routes/${productionRouteId}/finalized-reports/${finalizedReportId}`
    )
  }

  const finalizedReportAlert = () => {
    if (!productionRoutePeriodSummary.finalizedReportId) {
      return null
    }
    if (productionRoutePeriodSummary.finalizedReportStatus === 'PENDING') {
      return <Alert type="info">{t('reports:finalizedReportAlert.readyToBeFinalized')}</Alert>
    }
    if (productionRoutePeriodSummary.finalizedReportStatus === 'SUBMITTED') {
      return <Alert type="success">{t('reports:finalizedReportAlert.reportFinalized')}</Alert>
    }
  }

  const onMouseLeave = () => setHoveredPeriod(null)

  const canShowCreateReportButton =
    methodAggregatedEmissions &&
    !productionRoutePeriodSummary.finalizedReportId &&
    !areAllMonthsReported

  return (
    <>
      <BreadcrumbsBar
        links={[
          { label: t('common:breadcrumbs.dashboard'), to: `/dashboard` },
          {
            label: `${productionRoutePeriodSummary.productionRouteName} - ${periodLabel}`,
            to: ''
          }
        ]}
      />
      <Container>
        <header className="flex justify-between items-center">
          <div>
            <H2>{productionRoutePeriodSummary.productionRouteName}</H2>
            <p className="text-gray-600">{t('dashboard:title')}</p>
          </div>
          <div>
            {canShowCreateReportButton ? (
              <Button
                variant="subtle"
                sizeType="sm"
                icon={<Plus />}
                onClick={openCreateReportDialog}
              >
                {t('reports:createReportButton')}
              </Button>
            ) : (
              finalizedReportAlert()
            )}
          </div>
        </header>
        <div className="space-y-4">
          {methodAggregatedEmissions ? (
            <>
              <ProductionRoutePeriodSummary
                finalizedReportId={productionRoutePeriodSummary.finalizedReportId}
                finalizedReportStatus={productionRoutePeriodSummary.finalizedReportStatus}
                reportingPeriodBasis={reportingPeriodBasis}
                periodCoverage={coverage}
                activityLevel={methodAggregatedEmissions.activityLevel}
                specificEmbeddedDirect={methodAggregatedEmissions.specificEmbeddedDirect}
                specificEmbeddedIndirect={methodAggregatedEmissions.specificEmbeddedIndirect}
                beginFinalization={openFinalizeReportDialog}
                viewFinalizedReport={navigateToFinalizedReport}
                onMouseEnter={setHoveredPeriod}
                onMouseLeave={onMouseLeave}
              />
              <div className="space-y-1">
                {reportsForProductionRoutePeriod.map(report => (
                  <ReportListItem
                    key={report.id}
                    report={report}
                    onDelete={onDeleteReport}
                    hoveredPeriod={hoveredPeriod}
                  />
                ))}
              </div>
            </>
          ) : (
            <Card>
              <div className="py-8 flex flex-col gap-3 justify-center w-full items-center">
                <h4 className="font-semibold">{t('reports:beginReporting', { periodLabel })}</h4>
                <Button onClick={openCreateReportDialog}>{t('reports:createReportButton')}</Button>
              </div>
            </Card>
          )}
        </div>
      </Container>
      <CreateReportDialog
        isOpen={isCreateReportDialogOpen}
        close={closeCreateReportDialog}
        productionRouteName={productionRoutePeriodSummary.productionRouteName}
        periodLabel={periodLabel}
        monthsCoverage={coverage}
        startDate={startDate}
        endDate={endDate}
        installationId={installationId}
        productionRouteId={productionRouteId}
      />
      <CreateFinalizeReportDialog
        isOpen={isFinalizeReportDialogOpen}
        close={closeFinalizeReportDialog}
        startDate={startDate.toISOString()}
        endDate={endDate.toISOString()}
        installationId={installationId}
        productionRouteId={productionRouteId}
        productionMethodCodes={productionRoutePeriodSummary.methodAggregatedEmissions.map(
          method => method.productionMethodCode
        )}
        methodAggregatedEmissions={productionRoutePeriodSummary.methodAggregatedEmissions}
        aggregatedElectricity={productionRoutePeriodSummary.aggregatedElectricity}
      />
      <DeleteReportDialog
        isOpen={isDeleteReportDialogOpen}
        installationId={installationId}
        productionRouteId={productionRouteId}
        reportId={reportToDelete.id}
        reportPeriod={reportToDelete.formattedReportPeriod}
        close={onDeleteReportDialogClose}
      />
    </>
  )
}
